import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '../components/shared/Navigation';
import Footer from '../components/shared/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ApplicationPage() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [furnishingType, setFurnishingType] = useState('unfurnished'); // 'unfurnished' or 'furnished'
  const [formData, setFormData] = useState({
    applicant_name: '',
    applicant_email: '',
    applicant_phone: '',
    date_of_birth: '',
    ssn: '',
    drivers_license: '',
    current_address: '',
    current_landlord_name: '',
    current_landlord_phone: '',
    reason_for_moving: '',
    employment: '',
    income: '',
    move_in_date: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    pets: '',
    vehicles: '',
    total_occupants: '',
    number_of_adults: '',
    all_occupants_details: ''
  });

  // Calculate total application fee based on number of adults
  const totalApplicationFee = formData.number_of_adults ? parseInt(formData.number_of_adults) * 100 : 100;

  const isDepositOnly = searchParams.get('step') === 'deposit';

  useEffect(() => {
    fetch(`${API_URL}/api/units/${unitId}`)
      .then(res => res.json())
      .then(data => setUnit(data))
      .catch(err => console.error(err));
  }, [unitId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create application
      const appResponse = await fetch(`${API_URL}/api/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, unit_id: unitId })
      });

      const appData = await appResponse.json();

      if (!appResponse.ok) {
        throw new Error(appData.detail || 'Failed to create application');
      }

      // Create payment checkout
      const paymentResponse = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_type: isDepositOnly ? 'deposit' : 'application_fee',
          unit_id: unitId,
          application_id: appData.id,
          origin_url: window.location.origin,
          amount: isDepositOnly ? 250 : totalApplicationFee,
          number_of_applicants: formData.number_of_adults || 1
        })
      });

      const paymentData = await paymentResponse.json();

      if (!paymentResponse.ok) {
        throw new Error(paymentData.detail || 'Failed to create payment');
      }

      // Redirect to Stripe
      window.location.href = paymentData.url;

    } catch (error) {
      console.error(error);
      toast.error(error.message || 'An error occurred');
      setLoading(false);
    }
  };

  if (!unit) {
    return (
      <div>
        <Navigation />
        <div className="container section-spacing text-center">Loading...</div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      
      <div className="container section-spacing max-w-2xl">
        <div className="mb-8">
          <h1 className="text-4xl font-[var(--font-display)] tracking-[-0.02em] mb-2">
            {isDepositOnly ? 'Reserve Unit' : 'Apply for Unit'}
          </h1>
          <p className="text-muted-foreground">Unit {unit.unit_number}</p>
        </div>

        {/* Furnished/Unfurnished Toggle */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Select Rental Option</CardTitle>
            <CardDescription>Choose furnished or unfurnished rental</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFurnishingType('unfurnished')}
                className={`p-6 border-2 rounded-lg transition-all ${
                  furnishingType === 'unfurnished'
                    ? 'border-[#A06A3B] bg-[#A06A3B]/5'
                    : 'border-border hover:border-[#A06A3B]/50'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl font-[var(--font-display)] text-[#A06A3B] mb-2">
                    $2,250<span className="text-base">/mo</span>
                  </div>
                  <div className="font-semibold mb-1">Unfurnished</div>
                  <div className="text-sm text-muted-foreground">Bring your own furniture</div>
                  {furnishingType === 'unfurnished' && (
                    <div className="mt-3 text-[#A06A3B] font-semibold text-sm">✓ Selected</div>
                  )}
                </div>
              </button>
              
              <button
                type="button"
                onClick={() => setFurnishingType('furnished')}
                className={`p-6 border-2 rounded-lg transition-all ${
                  furnishingType === 'furnished'
                    ? 'border-[#A06A3B] bg-[#A06A3B]/5'
                    : 'border-border hover:border-[#A06A3B]/50'
                }`}
              >
                <div className="text-center">
                  <div className="text-2xl font-[var(--font-display)] text-[#A06A3B] mb-2">
                    $3,000<span className="text-base">/mo</span>
                  </div>
                  <div className="font-semibold mb-1">Fully Furnished</div>
                  <div className="text-sm text-muted-foreground">Designer furniture included</div>
                  {furnishingType === 'furnished' && (
                    <div className="mt-3 text-[#A06A3B] font-semibold text-sm">✓ Selected</div>
                  )}
                </div>
              </button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Applicant Information</CardTitle>
            <CardDescription>
              {isDepositOnly ? 'Provide your details to pay the $250 deposit' : 'Complete your application and pay the $100 application fee'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="applicant_name">Full Legal Name *</Label>
                  <Input
                    id="applicant_name"
                    required
                    value={formData.applicant_name}
                    onChange={(e) => setFormData({...formData, applicant_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="date_of_birth">Date of Birth *</Label>
                  <Input
                    id="date_of_birth"
                    type="date"
                    required
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="applicant_email">Email Address *</Label>
                  <Input
                    id="applicant_email"
                    type="email"
                    required
                    value={formData.applicant_email}
                    onChange={(e) => setFormData({...formData, applicant_email: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="applicant_phone">Phone Number *</Label>
                  <Input
                    id="applicant_phone"
                    type="tel"
                    required
                    value={formData.applicant_phone}
                    onChange={(e) => setFormData({...formData, applicant_phone: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ssn">Social Security Number *</Label>
                  <Input
                    id="ssn"
                    type="text"
                    required
                    maxLength="11"
                    value={formData.ssn}
                    onChange={(e) => setFormData({...formData, ssn: e.target.value})}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Required for background check</p>
                </div>
                <div>
                  <Label htmlFor="drivers_license">Driver's License Number *</Label>
                  <Input
                    id="drivers_license"
                    required
                    value={formData.drivers_license}
                    onChange={(e) => setFormData({...formData, drivers_license: e.target.value})}
                  />
                </div>
              </div>

              {/* Current Residence Section */}
              <div className="border-t border-border pt-6 mt-6">
                <h3 className="font-semibold mb-4 text-lg">Current Residence</h3>
                <div className="mb-4">
                  <Label htmlFor="current_address">Current Address *</Label>
                  <Input
                    id="current_address"
                    required
                    value={formData.current_address}
                    onChange={(e) => setFormData({...formData, current_address: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="current_landlord_name">Current Landlord Name</Label>
                    <Input
                      id="current_landlord_name"
                      value={formData.current_landlord_name}
                      onChange={(e) => setFormData({...formData, current_landlord_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="current_landlord_phone">Landlord Phone</Label>
                    <Input
                      id="current_landlord_phone"
                      type="tel"
                      value={formData.current_landlord_phone}
                      onChange={(e) => setFormData({...formData, current_landlord_phone: e.target.value})}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <Label htmlFor="reason_for_moving">Reason for Moving</Label>
                  <Input
                    id="reason_for_moving"
                    value={formData.reason_for_moving}
                    onChange={(e) => setFormData({...formData, reason_for_moving: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="move_in_date">Desired Move-in Date</Label>
                  <Input
                    id="move_in_date"
                    type="date"
                    value={formData.move_in_date}
                    onChange={(e) => setFormData({...formData, move_in_date: e.target.value})}
                  />
                </div>
              </div>

              {/* Employment & Income Section */}
              {!isDepositOnly && (
                <div className="border-t border-border pt-6 mt-6">
                  <h3 className="font-semibold mb-4 text-lg">Employment & Income</h3>
                  <div className="mb-4">
                    <Label htmlFor="employment">Current Employer *</Label>
                    <Input
                      id="employment"
                      required
                      value={formData.employment}
                      onChange={(e) => setFormData({...formData, employment: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="income">Annual Income (Before Taxes) *</Label>
                    <Input
                      id="income"
                      type="number"
                      required
                      value={formData.income}
                      onChange={(e) => setFormData({...formData, income: e.target.value})}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Minimum income requirement: 3x monthly rent
                    </p>
                  </div>
                </div>
              )}

              {/* Emergency Contact & Additional Info */}
              <div className="border-t border-border pt-6 mt-6">
                <h3 className="font-semibold mb-4 text-lg">Emergency Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="emergency_contact_name">Emergency Contact Name *</Label>
                    <Input
                      id="emergency_contact_name"
                      required
                      value={formData.emergency_contact_name}
                      onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="emergency_contact_phone">Emergency Contact Phone *</Label>
                    <Input
                      id="emergency_contact_phone"
                      type="tel"
                      required
                      value={formData.emergency_contact_phone}
                      onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Pets & Vehicles */}
              <div className="border-t border-border pt-6 mt-6">
                <h3 className="font-semibold mb-4 text-lg">Pets & Vehicles</h3>
                <div className="mb-4">
                  <Label htmlFor="pets">Pets</Label>
                  <Input
                    id="pets"
                    value={formData.pets}
                    onChange={(e) => setFormData({...formData, pets: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="vehicles">Vehicles</Label>
                  <Input
                    id="vehicles"
                    value={formData.vehicles}
                    onChange={(e) => setFormData({...formData, vehicles: e.target.value})}
                  />
                </div>
              </div>

              <div className="border-t border-border pt-6 mt-6">
                <h3 className="font-semibold mb-4 text-lg">All Occupants</h3>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-900 font-semibold mb-2">
                    ⚠️ IMPORTANT: You MUST list ALL people who will be living in this unit
                  </p>
                  <ul className="text-xs text-red-800 space-y-1 list-disc list-inside">
                    <li>All adults (18+) including yourself, spouse, partner, roommates, relatives</li>
                    <li>All children (under 18)</li>
                    <li>Each adult (18+) will be charged a $100 background check fee (collected upfront)</li>
                    <li>Failure to disclose all occupants may result in application denial or lease termination</li>
                  </ul>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="total_occupants">Total Number of People (including yourself) *</Label>
                    <Input
                      id="total_occupants"
                      type="number"
                      min="1"
                      max="15"
                      required
                      value={formData.total_occupants}
                      onChange={(e) => setFormData({...formData, total_occupants: e.target.value})}
                    />
                    <p className="text-xs text-muted-foreground mt-1">Include all adults and children</p>
                  </div>
                  <div>
                    <Label htmlFor="number_of_adults">Number of Adults 18+ (including yourself) *</Label>
                    <Input
                      id="number_of_adults"
                      type="number"
                      min="1"
                      max="15"
                      required
                      value={formData.number_of_adults}
                      onChange={(e) => setFormData({...formData, number_of_adults: e.target.value})}
                    />
                    <p className="text-xs text-red-600 font-semibold mt-1">
                      Fee: ${formData.number_of_adults ? parseInt(formData.number_of_adults) * 100 : 100} total
                    </p>
                  </div>
                </div>

                {formData.total_occupants && parseInt(formData.total_occupants) > 1 && (
                  <div>
                    <Label htmlFor="all_occupants_details">Complete Information for ALL Additional Occupants *</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      For EACH person (besides yourself), provide:
                    </p>
                    <ul className="text-xs text-muted-foreground mb-3 list-disc list-inside space-y-1">
                      <li>Full legal name</li>
                      <li>Date of birth</li>
                      <li>Relationship to you</li>
                      <li>Phone number (for adults 18+)</li>
                      <li>Email address (for adults 18+)</li>
                      <li>Social Security Number (for adults 18+ only - required for background check)</li>
                    </ul>
                    <textarea
                      id="all_occupants_details"
                      required
                      className="w-full min-h-[200px] p-3 border border-input rounded-md font-mono text-sm"
                      value={formData.all_occupants_details}
                      onChange={(e) => setFormData({...formData, all_occupants_details: e.target.value})}
                    />
                  </div>
                )}
              </div>

              {/* Approval Disclaimer */}
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-yellow-900 mb-2">
                  ⚠️ APPLICATION DOES NOT GUARANTEE APPROVAL
                </p>
                <p className="text-xs text-yellow-800">
                  Submitting this application and paying the application fee does NOT guarantee approval. 
                  All requirements must be met, including but not limited to: credit check, income verification, 
                  rental history, and background check clearance. Application fees are non-refundable regardless of approval status.
                </p>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Total Payment Amount</span>
                  <span className="text-2xl font-[var(--font-display)] text-[#A06A3B]">
                    ${isDepositOnly ? '250' : totalApplicationFee}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {isDepositOnly 
                    ? 'Non-refundable deposit to reserve this unit' 
                    : `Application fee: $100 × ${formData.number_of_adults || '1'} adult${parseInt(formData.number_of_adults) > 1 ? 's' : ''}`
                  }
                </p>
                <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Secure payment via Stripe
                </p>
              </div>

              <Button
                data-testid="application-form-submit-button"
                type="submit"
                className="w-full bg-[#A06A3B] text-[#FBF8F2] hover:bg-[#6F4526] shadow-[0_14px_30px_rgba(160,106,59,0.22)] rounded-[var(--btn-radius)]"
                disabled={loading}
              >
                {loading ? 'Processing...' : `Continue to Payment ($${isDepositOnly ? '250' : totalApplicationFee})`}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
}